import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Message, Segment } from 'semantic-ui-react';
import { iRootState } from '../../store';
import { CustomDropDownOptions } from '../../types';
import { dangerNotification, successNotification } from '../../services/notification';
import { toAbsoluteUrl } from '../../utils/toAbsoluteUrl';
import { UserRolesEnum } from '../../enums/user-roles.enum';
import Button from '../../components/Button';
import {
  createStandaloneChargeRefund,
  StandaloneChargeDTO
} from '../../services/standalone-charges';
import { postFileAxios } from '../../services/file';
import { exportStandaloneChargesReport, ListStandaloneChargesFiltersDTO } from '../../services/standalone-charges';
import { unmaskCpfOrCnpj } from '../../services/masks';
import { isBackofficeTestUser } from '../../util';

import {
  initialFilters,
  standaloneChargesLabel,
  statusLabelMap,
  statusOptions
} from './constants';
import {
  ChargeRefundData,
  StandaloneChargesLocationState,
  StandaloneChargesTab
} from './types';
import { useStandaloneChargesList } from './hooks/useStandaloneChargesList';
import { useChargeActions } from './hooks/useChargeActions';
import { useStandaloneChargesActivation } from './hooks/useStandaloneChargesActivation';
import { useCreateStandaloneChargeForm } from './hooks/useCreateStandaloneChargeForm';
import CreateChargeModal from './components/CreateChargeModal';
import SuccessModal from './components/SuccessModal';
import CancelChargeModal from './components/CancelChargeModal';
import FiltersBar from './components/FiltersBar';
import ChargesTable from './components/ChargesTable';
import ChargesTabs from './components/ChargesTabs';
import ChargeDetailsModal from './components/ChargeDetailsModal';
import KycBanner from './components/KycBanner';
import RetryBiometryConfirmModal from './components/RetryBiometryConfirmModal';
import ActivationGate from './components/ActivationGate/ActivationGate';
import ActivationWizardModal from './components/ActivationGate/ActivationWizardModal';
import ActivationStepContent from './components/ActivationGate/ActivationStepContent';
import CommissioningChargesList from './components/CommissioningChargesList';
import EstatesChargesList from './components/EstatesChargesList';
import EditBankAccountModal from './components/EditBankAccountModal';
import RefundChargeModal from './components/RefundChargeModal';

const mapState = (state: iRootState) => ({
  availableBrokersAndEstates: state.availableBrokersAndEstates,
  user: state.user
});

type Props = ReturnType<typeof mapState>;

const StandaloneChargesPage = ({
  availableBrokersAndEstates,
  user
}: Props) => {
  const location = useLocation<StandaloneChargesLocationState>();
  const createChargePrefillHandled = useRef(false);
  const createChargePrefill = location.state?.createChargePrefill;
  const lastActivationStep = 5;
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [createdChargeLink, setCreatedChargeLink] = useState('');
  const [createdPaymentMethod, setCreatedPaymentMethod] = useState<'PIX' | 'BOLETO'>(
    'PIX'
  );
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsChargeId, setDetailsChargeId] = useState<string | null>(null);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [chargeToRefund, setChargeToRefund] = useState<StandaloneChargeDTO | null>(null);
  const [refundSaving, setRefundSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<StandaloneChargesTab>(() =>
    new URLSearchParams(window.location.search).get('tab') === 'estates'
      ? 'estates'
      : 'sent'
  );
  const [exportLoading, setExportLoading] = useState(false);
  const [editEstateBankAccountOpen, setEditEstateBankAccountOpen] = useState(false);
  const [estatesListRefreshKey, setEstatesListRefreshKey] = useState(0);
  const adminOnboardingHandled = useRef(false);
  const role = user?.role as UserRolesEnum | undefined;
  const isBackofficeTest = isBackofficeTestUser(user);
  const isEstateUser = (role || '').includes('ESTATE');
  const isBrokerAdmin = role === UserRolesEnum.brokerAdmin;
  const isEstateAdmin = role === UserRolesEnum.estateAdmin;
  const isAdminUser =
    role === UserRolesEnum.brokerAdmin || role === UserRolesEnum.brokerAnalyst;
  const canManageActivation = isBrokerAdmin || isEstateUser;
  const onboardingEstateId = useMemo(
    () => new URLSearchParams(window.location.search).get('estateId') || '',
    []
  );
  const onboardingEstatesTab = useMemo(
    () => new URLSearchParams(window.location.search).get('tab') === 'estates',
    []
  );
  const needsActivationGate = isEstateUser;

  const estateOptions = useMemo(() => {
    return (availableBrokersAndEstates?.estates || []).map(
      (item: CustomDropDownOptions) => {
        const valueId = typeof item.value === 'string' ? item.value : item.value?.id;
        return {
          key: valueId || String(item.key),
          text: item.text || '',
          value: valueId || ''
        };
      }
    );
  }, [availableBrokersAndEstates?.estates]);

  const {
    isLoading,
    items,
    total,
    currentPage,
    setCurrentPage,
    filters,
    setFilters,
    appliedFilters,
    setAppliedFilters,
    loadData,
    handleSearch,
    clearFilters
  } = useStandaloneChargesList({
    initialFilters,
    enabled: !needsActivationGate
  });

  const activationEstateId = useMemo(() => {
    if (onboardingEstateId) {
      return onboardingEstateId;
    }

    if (isEstateUser) {
      return (
        availableBrokersAndEstates?.activeEstateId ||
        user?.estate?.id ||
        ''
      );
    }

    return filters.estateId || appliedFilters.estateId || '';
  }, [
    appliedFilters.estateId,
    availableBrokersAndEstates?.activeEstateId,
    filters.estateId,
    isEstateUser,
    onboardingEstateId,
    user?.estate?.id
  ]);

  const enableActivationManagement =
    needsActivationGate && Boolean(activationEstateId);

  const activation = useStandaloneChargesActivation({
    needsActivationGate,
    enableActivationManagement,
    activationEstateId,
    appliedFilters,
    onEnabled: () => {
      loadData();
      setEstatesListRefreshKey(key => key + 1);
    }
  });

  const {
    activationStatus,
    activationStatusError,
    activationLoading,
    activationSaving,
    activationForm,
    activationWizardOpen,
    activationStep,
    activationStepError,
    addressLookupLoading,
    addressLookupError,
    addressAutoFilled,
    canCreateCharges,
    isFeatureEnabled,
    activationIsPendingKyc,
    activationIsBiometryRejected,
    activationIsSupportRequired,
    canRetryBiometry,
    activationBiometryMeta,
    activationKycLink,
    activationKycPageUrl,
    activationKycQrImageUrl,
    hasLocalDraft,
    showActivationAction,
    activationActionLabel,
    loadActivationStatus,
    updateActivationFormField,
    openActivationWizard,
    closeActivationWizard,
    handleNextActivationStep,
    handleBackActivationStep,
    handleZipCodeLookup,
    handleActivate,
    retryBiometryModalOpen,
    retryBiometrySaving,
    openRetryBiometryModal,
    closeRetryBiometryModal,
    handleRetryBiometry
  } = activation;

  useEffect(() => {
    if (needsActivationGate && canCreateCharges) {
      loadData(currentPage, appliedFilters);
    }
  }, [needsActivationGate, canCreateCharges, currentPage, appliedFilters, loadData]);

  useEffect(() => {
    if (!role) return;
    if (!isAdminUser && activeTab === 'estates') {
      setActiveTab('sent');
    }
  }, [role, isAdminUser, activeTab]);

  useEffect(() => {
    if (adminOnboardingHandled.current) return;
    if (!onboardingEstateId || !isAdminUser || !onboardingEstatesTab) return;

    adminOnboardingHandled.current = true;
    setActiveTab('estates');
    if (isBrokerAdmin) {
      openActivationWizard(onboardingEstateId);
    }
  }, [
    isAdminUser,
    isBrokerAdmin,
    onboardingEstateId,
    onboardingEstatesTab,
    openActivationWizard
  ]);

  const openEstateInSentTab = (estateId: string) => {
    const nextFilters = { ...filters, estateId };
    const params = new URLSearchParams(window.location.search);
    params.set('estateId', estateId);
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}?${params.toString()}`
    );
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    setCurrentPage(1);
    setActiveTab('sent');
  };

  const {
    actionLoading,
    copyChargeLink,
    handleResendEmail,
    cancelModalOpen,
    chargeToCancel,
    openCancelModal,
    closeCancelModal,
    confirmCancelCharge,
    handleRenewCharge
  } = useChargeActions(
    () => loadData(),
    renewedCharge => {
      setCreatedChargeLink(renewedCharge.chargeLinkUrl);
      setCreatedPaymentMethod(renewedCharge.paymentMethod);
      setSuccessModalOpen(true);
    }
  );

  const needsOnboarding = !canCreateCharges || activationStatus?.activation == null;
  const isCreateBlockedByActivation = needsActivationGate && !canCreateCharges;
  const isCreateBlockedByPendingKyc = needsActivationGate && activationIsPendingKyc;
  const isCreateBlockedByBiometryIssue =
    needsActivationGate && (activationIsBiometryRejected || activationIsSupportRequired);
  const isCreateBlocked =
    isCreateBlockedByActivation ||
    isCreateBlockedByPendingKyc ||
    isCreateBlockedByBiometryIssue;
  const isEstateActivationComplete = Boolean(activationStatus?.standaloneChargesEnabled);
  const showTopKycBanner =
    isEstateUser &&
    (activationIsPendingKyc ||
      activationIsBiometryRejected ||
      activationIsSupportRequired);

  const getCreateBlockMessage = () => {
    if (activationIsBiometryRejected) {
      return canRetryBiometry
        ? 'A biometria foi recusada. Tente novamente para liberar a emissão de cobranças.'
        : 'Limite de tentativas de biometria atingido. Entre em contato com o suporte.';
    }
    if (activationIsSupportRequired) {
      return 'Seu cadastro precisa de análise manual. Entre em contato com o suporte.';
    }
    if (isCreateBlockedByPendingKyc) {
      return 'Conclua a biometria da Pagar.me para liberar a emissão de cobranças.';
    }
    if (isCreateBlockedByActivation) {
      return 'Conclua o cadastro de ativação para emitir cobranças.';
    }
    return '';
  };

  const userHasCreatePermission =
    isAdminUser ||
    (isEstateUser && canCreateCharges);
  const userCanSend = isAdminUser || isEstateUser;
  const userCanCancel = isAdminUser || isEstateUser;
  const userCanRenew = userHasCreatePermission;
  const userCanRefund = isAdminUser || isEstateUser;

  const createChargeForm = useCreateStandaloneChargeForm({
    isEstateUser,
    isBackofficeTest,
    defaultEstateId: appliedFilters.estateId || activationEstateId,
    isCreateBlocked,
    getCreateBlockMessage,
    onCreated: (chargeLinkUrl: string, paymentMethod: 'PIX' | 'BOLETO') => {
      setCreatedChargeLink(chargeLinkUrl);
      setCreatedPaymentMethod(paymentMethod);
      setSuccessModalOpen(true);
      setCurrentPage(1);
      loadData(1, appliedFilters);
    }
  });

  useEffect(() => {
    if (!createChargePrefill || createChargePrefillHandled.current) return;

    createChargePrefillHandled.current = true;
    createChargeForm.openCreateModal(createChargePrefill);
    // O preenchimento deve ser aplicado apenas uma vez ao entrar na tela.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createChargePrefill]);

  useEffect(() => {
    if (!createChargeForm.createModalOpen) return;
    setTimeout(() => {
      const firstField = document.getElementById('create-amount-input');
      firstField?.focus();
    }, 0);
  }, [createChargeForm.createModalOpen]);

  const openChargeDetails = (chargeId: string) => {
    setDetailsChargeId(chargeId);
    setDetailsModalOpen(true);
  };

  const closeChargeDetails = () => {
    setDetailsModalOpen(false);
    setDetailsChargeId(null);
  };

  const buildListFilters = (sourceFilters = filters): ListStandaloneChargesFiltersDTO => {
    const payerFilter = sourceFilters.type === 'payer' ? sourceFilters.argument : '';
    const documentFilter =
      sourceFilters.type === 'document' ? unmaskCpfOrCnpj(sourceFilters.argument) : '';
    const emailFilter = sourceFilters.type === 'email' ? sourceFilters.argument : '';

    return {
      estateId: sourceFilters.estateId || undefined,
      estateUnitId: sourceFilters.estateUnitId || undefined,
      payer: payerFilter || undefined,
      document: documentFilter || undefined,
      email: emailFilter || undefined,
      status: sourceFilters.status || undefined,
      startDate: sourceFilters.startDate || undefined,
      endDate: sourceFilters.endDate || undefined
    };
  };

  const handleExportReport = async () => {
    setExportLoading(true);

    try {
      await exportStandaloneChargesReport(buildListFilters());
      successNotification('Sucesso', 'Relatório de cobranças exportado.');
    } catch (error) {
      dangerNotification(
        'Erro',
        'Não foi possível exportar o relatório de cobranças.'
      );
    } finally {
      setExportLoading(false);
    }
  };

  const openRefundModal = (charge: StandaloneChargeDTO) => {
    setChargeToRefund(charge);
    setRefundModalOpen(true);
  };

  const closeRefundModal = () => {
    setChargeToRefund(null);
    setRefundModalOpen(false);
  };

  const confirmRefundCharge = async (refund: ChargeRefundData) => {
    setRefundSaving(true);
    try {
      const createdRefund = await createStandaloneChargeRefund(refund.chargeId, {
        amount: refund.amount,
        description: refund.description
      });

      await Promise.all(
        (refund.attachments || []).map(file => {
          const formData = new FormData();
          formData.append('file', file);
          return postFileAxios(formData, {
            tableName: 'standalone_charge_refunds',
            registerId: createdRefund.id,
            name: 'refund_attachment'
          });
        })
      );

      successNotification('Sucesso', 'Estorno registrado com sucesso!');
      closeRefundModal();
      loadData(currentPage, appliedFilters);
    } catch (error) {
      const message =
        typeof error === 'object' && error !== null && 'message' in error
          ? String((error as { message?: string }).message)
          : 'Não foi possível registrar o estorno.';
      dangerNotification('Erro', message);
    } finally {
      setRefundSaving(false);
    }
  };

  const openKycLink = () => {
    if (!activationKycLink) {
      dangerNotification('Atenção', 'Link de biometria indisponível no momento.');
      return;
    }
    window.open(toAbsoluteUrl(activationKycLink), '_blank', 'noopener,noreferrer');
  };

  const activationWizardContent = (
    <ActivationStepContent
      activationStep={activationStep}
      activationForm={activationForm}
      updateActivationFormField={updateActivationFormField}
      handleZipCodeLookup={handleZipCodeLookup}
      addressLookupLoading={addressLookupLoading}
      addressLookupError={addressLookupError}
      addressAutoFilled={addressAutoFilled}
    />
  );

  const activationWizardModal = (
    <ActivationWizardModal
      open={activationWizardOpen}
      onClose={closeActivationWizard}
      activationStep={activationStep}
      lastActivationStep={lastActivationStep}
      activationSaving={activationSaving}
      activationStepError={activationStepError}
      recipientType={activationForm.recipientType}
      onBack={handleBackActivationStep}
      onNext={handleNextActivationStep}
      onFinish={handleActivate}
      finishButtonText={
        activationIsPendingKyc ? 'Salvar alterações' : 'Concluir ativação'
      }
      closeOnDimmerClick={false}
    >
      {activationWizardContent}
    </ActivationWizardModal>
  );

  const sharedActivationGateProps = {
    featureEnabled: isFeatureEnabled,
    activationLoading,
    activationStatusError,
    activationStatus,
    activationIsPendingKyc,
    activationIsBiometryRejected,
    activationIsSupportRequired,
    canRetryBiometry,
    activationKycLink,
    activationKycPageUrl,
    activationKycQrImageUrl,
    openKycLink,
    loadActivationStatus,
    showActivationAction,
    activationActionLabel,
    hasLocalDraft,
    openActivationWizard,
    onRetryBiometry: openRetryBiometryModal,
    showEditRegistrationAction: canManageActivation,
    onEditRegistration: () => {
      void openActivationWizard();
    },
    showEditBankAccountAction: canManageActivation,
    onEditBankAccount: () => setEditEstateBankAccountOpen(true)
  };

  const retryBiometryModal = (
    <RetryBiometryConfirmModal
      open={retryBiometryModalOpen}
      loading={retryBiometrySaving}
      attemptCount={activationBiometryMeta?.recipientAttemptCount}
      maxAttempts={activationBiometryMeta?.maxRecipientAttempts}
      onConfirm={handleRetryBiometry}
      onClose={closeRetryBiometryModal}
    />
  );

  if (needsActivationGate && needsOnboarding) {
    return (
      <>
        <ActivationGate {...sharedActivationGateProps} />
        {activationWizardModal}
        {retryBiometryModal}
        <EditBankAccountModal
          open={editEstateBankAccountOpen}
          mode="estate"
          estateId={activationEstateId}
          source={
            activationStatus?.activation
              ? {
                  recipientType: activationStatus.activation.recipientType as
                    | 'individual'
                    | 'corporation'
                    | undefined,
                  registerInformation: activationStatus.activation.registerInformation,
                  defaultBankAccount: activationStatus.activation.defaultBankAccount,
                  payload: activationStatus.activation.payload
                }
              : null
          }
          onClose={() => setEditEstateBankAccountOpen(false)}
          onSuccess={async () => {
            await loadActivationStatus();
          }}
        />
      </>
    );
  }

  return (
    <>
      {showTopKycBanner && (
        <KycBanner
          openKycLink={openKycLink}
          loadActivationStatus={loadActivationStatus}
          activationLoading={activationLoading}
          activationKycPageUrl={activationKycPageUrl}
          onEditActivation={
            activationIsPendingKyc || activationIsBiometryRejected
              ? () => { void openActivationWizard(); }
              : undefined
          }
          activationIsBiometryRejected={activationIsBiometryRejected}
          activationIsSupportRequired={activationIsSupportRequired}
          canRetryBiometry={canRetryBiometry}
          activationBiometryMeta={activationBiometryMeta}
          onRetryBiometry={openRetryBiometryModal}
        />
      )}

      <ChargesTabs
        activeTab={activeTab}
        showEstatesTab={isAdminUser}
        onChange={setActiveTab}
      />

      {activeTab === 'sent' ? (
        <>
          <FiltersBar
            isEstateUser={isEstateUser}
            estateOptions={estateOptions}
            filters={filters}
            setFilters={setFilters}
            statusOptions={statusOptions}
            onSearch={handleSearch}
            onClear={clearFilters}
            onExport={handleExportReport}
            exportLoading={exportLoading}
          />

          <Segment>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 12,
                gap: 8,
                flexWrap: 'wrap'
              }}
            >
              <h3 style={{ margin: 0 }}>{standaloneChargesLabel}</h3>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {isEstateUser &&
                  isEstateActivationComplete &&
                  canManageActivation && (
                    <>
                      <Button
                        type="button"
                        light
                        text="Editar cadastro"
                        onClick={() => {
                          void openActivationWizard();
                        }}
                      />
                      <Button
                        type="button"
                        light
                        text="Alterar conta bancária"
                        onClick={() => setEditEstateBankAccountOpen(true)}
                      />
                    </>
                  )}
                {userHasCreatePermission && (
                  <Button
                    type="button"
                    text="Nova Cobrança"
                    onClick={() => createChargeForm.openCreateModal()}
                    disabled={isCreateBlocked}
                  />
                )}
              </div>
            </div>
            {isCreateBlocked && (
              <Message warning style={{ marginTop: 0 }}>
                <p style={{ margin: 0 }}>{getCreateBlockMessage()}</p>
              </Message>
            )}

            <ChargesTable
              isLoading={isLoading}
              items={items}
              statusLabelMap={statusLabelMap}
              currentPage={currentPage}
              total={total}
              setCurrentPage={setCurrentPage}
              actionLoading={actionLoading}
              userCanSend={userCanSend}
              userCanCancel={userCanCancel}
              userCanRenew={userCanRenew}
              userCanRefund={userCanRefund}
              showEstateColumn={!isEstateUser}
              onRowClick={openChargeDetails}
              onCopyLink={copyChargeLink}
              onResendEmail={handleResendEmail}
              onCancel={openCancelModal}
              onRenew={handleRenewCharge}
              onRefund={openRefundModal}
            />
          </Segment>
        </>
      ) : activeTab === 'commissioning' ? (
        <CommissioningChargesList
          isBrokerAdmin={isBrokerAdmin}
          isEstateAdmin={isEstateAdmin}
          defaultEstateId={activationEstateId || availableBrokersAndEstates?.activeEstateId || user?.estate?.id || ''}
          estateOptions={estateOptions}
          currentUserId={user?.id}
          currentUserDocument={user?.document}
        />
      ) : (
        <EstatesChargesList
          onOpenEstateInSentTab={openEstateInSentTab}
          onEditRegistration={
            isBrokerAdmin ? estateId => openActivationWizard(estateId) : undefined
          }
          canEditRegistration={isBrokerAdmin}
          canEditBankAccount={isBrokerAdmin}
          canResetAttempts={isBrokerAdmin}
          refreshKey={estatesListRefreshKey}
        />
      )}

      <CreateChargeModal
        open={createChargeForm.createModalOpen}
        onClose={createChargeForm.closeCreateModal}
        isEstateUser={isEstateUser}
        isBackofficeTest={isBackofficeTest}
        estateOptions={estateOptions}
        defaultEstateId={activationEstateId}
        form={createChargeForm.createForm}
        errors={createChargeForm.createFormErrors}
        saving={createChargeForm.savingCreate}
        onChangeField={createChargeForm.updateCreateFormField}
        onReview={createChargeForm.validateCreateForm}
        onSubmit={createChargeForm.handleCreateCharge}
        addressLookupLoading={createChargeForm.addressLookupLoading}
        addressLookupError={createChargeForm.addressLookupError}
        addressAutoFilled={createChargeForm.addressAutoFilled}
        onPayerAddressZipCodeInput={createChargeForm.onPayerAddressZipCodeInput}
      />

      <SuccessModal
        open={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        chargeLink={createdChargeLink}
        paymentMethod={createdPaymentMethod}
        onCopy={() => copyChargeLink(createdChargeLink)}
      />

      <CancelChargeModal
        open={cancelModalOpen}
        onClose={closeCancelModal}
        charge={chargeToCancel}
        loading={Boolean(actionLoading?.action === 'cancel')}
        onConfirm={confirmCancelCharge}
      />

      <RefundChargeModal
        open={refundModalOpen}
        charge={chargeToRefund}
        loading={refundSaving}
        onClose={closeRefundModal}
        onConfirm={confirmRefundCharge}
      />

      <ChargeDetailsModal
        open={detailsModalOpen}
        chargeId={detailsChargeId}
        statusLabelMap={statusLabelMap}
        hideEstateSection={isEstateUser}
        onClose={closeChargeDetails}
        onCopyLink={copyChargeLink}
      />

      {activationWizardModal}
      {retryBiometryModal}
      <EditBankAccountModal
        open={editEstateBankAccountOpen}
        mode="estate"
        estateId={activationEstateId}
        source={
          activationStatus?.activation
            ? {
                recipientType: activationStatus.activation.recipientType as
                  | 'individual'
                  | 'corporation'
                  | undefined,
                registerInformation: activationStatus.activation.registerInformation,
                defaultBankAccount: activationStatus.activation.defaultBankAccount,
                payload: activationStatus.activation.payload
              }
            : null
        }
        onClose={() => setEditEstateBankAccountOpen(false)}
        onSuccess={async () => {
          await loadActivationStatus();
        }}
      />
    </>
  );
};

export default connect(mapState)(StandaloneChargesPage);
